import ViewListIcon from '@mui/icons-material/ViewList';
import ViewHeadlineIcon from '@mui/icons-material/ViewHeadline';
import React, { useEffect, useState } from 'react';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { TCourseViewModel } from '../../../../services/models/CourseViewModels';
import { useStore } from '../../../../store/store';
/* import CoursePageList, { TreeItem } from '../CoursePages/CoursePageList'; */
import { ICourseProperties } from '../../../../services/models/PropertyViewModel';
import Typography from '@mui/material/Typography/Typography';
import Divider from '@mui/material/Divider/Divider';
import Button from '@mui/material/Button/Button';
import TextField from '@mui/material/TextField/TextField';
import { runInAction } from 'mobx';
import { createUseStyles } from 'react-jss';
import { Theme, useTheme } from '@mui/material/styles';
import { useSnackbar } from 'notistack';
import SettingsIcon from '@mui/icons-material/Settings';
import { observer } from 'mobx-react';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton/IconButton';
import { ICopyPageObject } from '../../../../services/models/OwnerViewModel';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import SnackbarUtils from '../../../../components/snackbarUtils/SnackbarUtilsConfigurator';
import DashboardPagePropertiesDrawer from '../../../../features/properties/pageProperties/DashboardPagePropertiesDrawer';
import CoursePageList, { IPageItem } from '../coursePages/CoursePageList';
import useCookieState from '../../../../hooks/useCookieState';

interface IEditCourseViewProps {
  courseViewModel: TCourseViewModel;
  courseProperties: ICourseProperties;
}

type PageViewType = 'list' | 'imageList';

export default observer(function EditCourseView({ courseViewModel, courseProperties }: IEditCourseViewProps) {
  const { courseStore, propertyStore } = useStore();
  const { newEmptyPage, deselectPageProperties, copyPage } = courseStore;
  const { setProperty } = propertyStore;
  const [pagesCount, setPagesCount] = useState(0);
  const [pageView, setPageView] = useCookieState<PageViewType>(`pageView-${courseViewModel.courseID}`, 'list');
  const [courseViewHeight, setCourseViewHeight] = useState(window.innerHeight - 300 > 200 ? window.innerHeight - 300 : 200);
  const [courseTitle, setCourseTitle] = useState(courseProperties.descriptions.items.title.option.value);
  const [editTitleMode, setEditTitleMode] = useState(false);

  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCourseTitle(event.target.value);
  };

  const updateTitle = () => {
    if (editTitleMode) {
      setProperty(courseProperties.descriptions.items.title, courseTitle);
      runInAction(() => {
        courseProperties.descriptions.items.title.option.value = courseTitle;
      });
    }
    setEditTitleMode(false);
  };

  const handleOnKeyDown = (e: any) => {
    if (editTitleMode && e.keyCode === 13) {
      updateTitle();
    }
  };

  const handleNewPageButton = () => {
    newEmptyPage(pagesCount, undefined);
  };

  const handlePageView = (event: React.MouseEvent<HTMLElement>, aPageView: PageViewType | null) => {
    if (aPageView !== null) {
      if (document.startViewTransition) {
        document.startViewTransition(() => {
          setPageView(aPageView);
        });
      } else {
        // Fallback: no transition support
        setPageView(aPageView);
      }
      setProperty(courseProperties.settings.items.pageView, aPageView);
    }
  };

  const handleNewPageItem = (item: IPageItem) => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    if (item.data === undefined) {
      //newEmptyPage(0);
    } else {
      let index = item.data.index + 1;
      let parentCourseID = item.data.parentCoursePageID ? item.data.parentCoursePageID : 0;

      if (item.children.length > 0 && item.isExpanded) {
        index = 1;
        parentCourseID = parseInt(item.data.id);
      }

      newEmptyPage(index, parentCourseID);
      //const parentCourseID = item.isExpanded ? (item.id as number) : item.data.parentCoursePageID;
      //const parentCourseID = item.data.parentCoursePageID ? item.data.parentCoursePageID : item.isExpanded && item.hasChildren ? (item.id as number) : 0;
      //const index = item.data.index;
    }
  };

  const handleDuplicatePage = (aPage: IPageItem) => {
    const pageID = parseInt(aPage.id + '');

    if (isNaN(pageID)) {
      console.log('unable to parse pageID as number.');
      enqueueSnackbar('Error copying page.', {
        variant: 'error',
      });
      return;
    }
    const copyObj: ICopyPageObject = {
      source: { courseID: courseViewModel.courseID, coursePageID: pageID },
      dest: { courseID: courseViewModel.courseID, coursePageID: aPage.data?.parentCoursePageID ? aPage.data.parentCoursePageID : 0 },
      copyChildPages: false,
      index: aPage.data?.index ? aPage.data.index + 1 : pagesCount,
      numberOfChildren: 0,
    };
    copyPage(copyObj);
  };

  const handleCopyPage = (aPage: IPageItem, aCopyChildren: boolean) => {
    const pageID = aPage.key as number;

    if (isNaN(pageID)) {
      console.log('unable to parse pageID as number.');
      enqueueSnackbar('Error copying page.', {
        variant: 'error',
      });
      return;
    }

    const copyObj: ICopyPageObject = {
      source: { courseID: courseViewModel.courseID, coursePageID: pageID },
      dest: { courseID: 0, coursePageID: 0 },
      copyChildPages: aCopyChildren,
      index: 0,
      numberOfChildren: 0,
    };
    if (aCopyChildren) {
      copyObj.numberOfChildren = 0 /* checkNumberOfChildren(courseViewModel.pages, aPage.id) */;
    }
    navigator.clipboard.writeText(JSON.stringify(copyObj));
    SnackbarUtils.info('Copied to clipboard');
  };

  const resizeCourseView = () => {
    setCourseViewHeight(window.innerHeight - 300 > 200 ? window.innerHeight - 300 : 200);
  };

  const handleCopyPastePage = async (item?: IPageItem) => {
    // Read clipboard data
    navigator.clipboard
      .readText()
      .then((clipboardData) => {
        try {
          const parsedCopyObj: ICopyPageObject = JSON.parse(clipboardData);
          // Validating structure
          let index = pagesCount;
          if (parsedCopyObj.source && parsedCopyObj.source.courseID !== undefined && parsedCopyObj.source.coursePageID !== undefined && parsedCopyObj.copyChildPages !== undefined) {
            if (item?.data !== undefined) {
              index = item.data.index + 1;
              let parentCourseID = item.data.parentCoursePageID ? item.data.parentCoursePageID : 0;

              if (item.children.length > 0 && item.isExpanded) {
                index = 0;
                parentCourseID = item.key as number;
              }

              parsedCopyObj.dest.coursePageID = parentCourseID;
              const parentCount = 0; /* checkNumberOfParents(courseViewModel.pages, item.id) */
              parsedCopyObj.numberOfChildren = parsedCopyObj.numberOfChildren + parentCount - 1; //-1 fordi den teller med root.
            }

            parsedCopyObj.index = index;
            parsedCopyObj.dest.courseID = courseViewModel.courseID;

            if (parsedCopyObj.numberOfChildren < 3) {
              copyPage(parsedCopyObj);
            } else {
              // eslint-disable-next-line quotes
              SnackbarUtils.info('Maximum indent level reached. Please paste at a higher level.', undefined, undefined, 5000);
            }

            return;
          } else {
            SnackbarUtils.error('Invalid page structure in clipboard. Please copy pages again and retry');
            return;
          }
        } catch (err) {
          SnackbarUtils.error('Invalid page structure in clipboard. Please copy pages again and retry');
          return;
        }
      })
      .catch((error) => {
        const errorString = error + '';

        if (errorString.includes('NotAllowedError')) {
          // eslint-disable-next-line quotes
          SnackbarUtils.error("We don't have access to your clipboard. Please reset browser permissions or duplicate page instead.", () => openWiki(), 'reset', 10000);
        }
      });
  };

  const openWiki = async () => {
    window.open('https://norskinteraktiv.atlassian.net/wiki/spaces/CBP/pages/2557870093/Creator+Essentials+Startup+Menu', '_blank')?.focus();
  };

  const handleDeselectPageProperties = (e: any) => {
    if (e.target.getAttribute('data-clickable') === 'true') {
      deselectPageProperties();
    }
  };

  const useStyles = createUseStyles((theme: Theme) => ({
    title: {
      color: theme.palette.text.primary,
    },
    divider: {
      borderColor: theme.palette.grey[600],
      borderBottomWidth: 2,
    },
    caption: {
      color: theme.palette.grey[600],
    },
    box: {
      padding: theme.spacing(2),
      paddingTop: theme.spacing(3),
      gridArea: '2 / 1 / 3 / 2',
      display: 'grid',
      boxSizing: 'border-box',
      gridTemplateRows: 'auto 1fr',
    },
    content: {
      marginTop: 20,
      paddingTop: 5,
      paddingLeft: theme.spacing(5.5),
      paddingRight: theme.spacing(10),
      overflow: 'auto',
    },
    conentTitle: {
      display: 'flex',
      alignItems: 'start',
      justifyContent: 'space-between',
      paddingLeft: theme.spacing(10),
      paddingRight: theme.spacing(10),
    },
    viewGroup: {
      display: 'flex',
      justifyContent: 'space-between',
      padding: theme.spacing(2),
      gridArea: '1 / 1 / 2 / 2',
    },
    drawer: {
      background: theme.palette.bgTwo.main,
      padding: 16,
      boxSizing: 'border-box',
      display: 'flex',
      gridArea: '1 / 2 / 3 / 3',
      flexGrow: 1,
      flexDirection: 'column',
      overflow: 'auto',
    },
    tooltip: {
      fontSize: theme.typography.pxToRem(15),
    },
    iconButton: {
      color: theme.palette.darkGray.main,
      margin: 5,
      '&:hover': {
        backgroundColor: 'transparent',
        color: theme.palette.darkGray.light,
      },
    },
  }));
  const classes = useStyles({ theme });

  useEffect(() => {
    resizeCourseView();
    window.addEventListener('resize', resizeCourseView, { passive: false });
    return () => {
      window.removeEventListener('resize', resizeCourseView);
    };
  }, []);

  useEffect(() => {
    setPagesCount(courseViewModel.pages.length + 1);
  }, [courseViewModel]);

  return (
    <>
      <div
        data-clickable='true'
        style={{ flex: '1 1 auto', display: 'grid', gridAutoColumns: '1fr', gridTemplateColumns: '1fr 400px', gridTemplateRows: '72px minmax(0, 1fr)' }}
        onClick={handleDeselectPageProperties}
      >
        <div data-clickable='true' className={classes.viewGroup}>
          <div>
            <Tooltip title='Course Properties' placement='top' tabIndex={0} arrow={true} classes={{ tooltip: classes.tooltip }}>
              <IconButton size='small' className={classes.iconButton} onClick={deselectPageProperties}>
                <SettingsIcon fontSize='inherit' />
              </IconButton>
            </Tooltip>
            <Tooltip
              title='Paste Page(s)'
              placement='top'
              tabIndex={1}
              arrow={true}
              classes={{ tooltip: classes.tooltip }}
              PopperProps={{
                disablePortal: true,
              }}
            >
              <IconButton size='small' className={classes.iconButton} onClick={() => handleCopyPastePage()}>
                <ContentPasteIcon fontSize='inherit' />
              </IconButton>
            </Tooltip>
          </div>
          <ToggleButtonGroup size='small' value={pageView} exclusive onChange={handlePageView} aria-label='text alignment'>
            <ToggleButton value='list' aria-label='List layout'>
              <Tooltip title='List'>
                <ViewHeadlineIcon />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value='imageList' aria-label='List with thumbnails layout'>
              <Tooltip title='Thumbnails'>
                <ViewListIcon />
              </Tooltip>
            </ToggleButton>
            {/*             <ToggleButton value='tile' aria-label='left aligned'>
              <ViewModuleIcon />
            </ToggleButton> */}
          </ToggleButtonGroup>
        </div>

        <div className={classes.box} data-clickable='true'>
          {courseViewModel && (
            <>
              <div className={classes.conentTitle}>
                <div style={{ flex: '1 1 auto', marginRight: 10 }}>
                  {!editTitleMode ? (
                    <>
                      <Typography variant='h4' className={classes.title} onClick={() => setEditTitleMode(true)}>
                        {courseProperties.descriptions.items.title.option.value}
                      </Typography>
                      <Divider className={classes.divider} />
                    </>
                  ) : (
                    <>
                      <TextField
                        data-pagetour='2'
                        variant={'standard'}
                        size={'small'}
                        inputProps={{ style: { fontSize: '2.0243rem' } }}
                        hiddenLabel={true}
                        value={courseTitle}
                        onChange={handleChange}
                        onKeyDown={handleOnKeyDown}
                        onBlur={updateTitle}
                        fullWidth
                        autoFocus
                      />
                    </>
                  )}

                  {/* <Typography variant='caption' className={classes.caption}>
                    ENGLISH
                  </Typography> */}
                  <div style={{ height: 22 }}></div>
                </div>
                <Button style={{ maxHeight: 36.5, minWidth: 144.67 }} data-pagetour='1' color='primary' variant='contained' onClick={handleNewPageButton}>
                  ADD NEW PAGE
                </Button>
              </div>
              <div className={classes.content} data-clickable='true'>
                <CoursePageList
                  pages={courseViewModel.pages}
                  height={courseViewHeight}
                  handleNewPageItem={handleNewPageItem}
                  handlePastePage={handleCopyPastePage}
                  handleCopyPageItem={handleCopyPage}
                  handleDuplicatePageItem={handleDuplicatePage}
                  image={pageView === 'imageList'}
                />
                {/*                 {pageView === 'thicList' && (
                  <CoursePageList
                    pages={courseViewModel.pages}
                    height={courseViewHeight}
                    handleNewPageItem={handleNewPageItem}
                    handlePastePage={handleCopyPastePage}
                    handleCopyPageItem={handleCopyPage}
                    handleDuplicatePageItem={handleDuplicatePage}
                    image
                  />
                )} */}
                {/*                 {pageView === 'tile' && (
                  <CoursePageGrid
                    pages={courseViewModel.pages}
                    height={courseViewHeight}
                    handleNewPageItem={handleNewPageItem}
                    handlePastePage={handleCopyPastePage}
                    handleCopyPageItem={handleCopyPage}
                  />
                )} */}
              </div>
            </>
          )}
        </div>

        <div className={classes.drawer}>
          <DashboardPagePropertiesDrawer />
        </div>
      </div>
    </>
  );
});
